import React from 'react';
import {
  Box, Paper, Typography, Divider, Button, FormControlLabel, Switch, Chip
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Calculate as CalculateIcon,
  PointOfSale as PointOfSaleIcon
} from '@mui/icons-material';

/**
 * Paper "Resumen del Comprobante": desglose de subtotales, IVA/retención
 * según el tipo de DTE, total general, abonado y saldo pendiente.
 */
const AlbaranResumenPanel = ({
  tipoDte,
  resumenTotales,
  aplicaRetencion,
  setAplicaRetencion,
  onAbrirCalculadora,
}) => {
  const theme = useTheme();
  const etiquetaTipo = tipoDte === '03' ? 'CCF (03)' : tipoDte === '11' ? 'FEX (11)' : 'Factura (01)';

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)',
      }}
    >
      {/* Header Unificado */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justify: 'space-between',
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.common.black, 0.01),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              p: 0.8,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              display: 'flex',
            }}
          >
            <PointOfSaleIcon fontSize="small" />
          </Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Resumen del Comprobante
          </Typography>
        </Box>
        <Chip
          size="small"
          label={etiquetaTipo}
          sx={{ fontWeight: 700, bgcolor: 'action.selected' }}
        />
      </Box>

      {/* Contenido / Desglose */}
      <Box sx={{ p: 2.5 }}>
        {/* Control de Retención visible únicamente en CCF cuando el subtotal es >= $100.00 */}
        {tipoDte === '03' && resumenTotales.baseImponible >= 100.0 && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.06),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={aplicaRetencion}
                  onChange={(e) => setAplicaRetencion(e.target.checked)}
                  color="warning"
                  size="small"
                />
              }
              label={
                <Typography variant="body2" fontWeight={600} color="warning.dark">
                  Gran Contribuyente / Agente Retención (1%)
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </Box>
        )}

        {/* Subtotal Bruto */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Subtotal:
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            ${resumenTotales.subtotalBruto.toFixed(2)}
          </Typography>
        </Box>

        {/* Descuentos */}
        {resumenTotales.totalDescuentos > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Descuento Total:
            </Typography>
            <Typography variant="body2" fontWeight={700} color="error.main">
              -${resumenTotales.totalDescuentos.toFixed(2)}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 1.2, borderColor: alpha(theme.palette.divider, 0.6) }} />

        {/* Base Imponible */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.primary" fontWeight={700}>
            {tipoDte === '03' ? 'Base Imponible (Gravada):' : 'Total Operación'}
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            ${resumenTotales.baseImponible.toFixed(2)}
          </Typography>
        </Box>

        {/* IVA 13% en CCF */}
        {tipoDte === '03' && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              (+) IVA Débito Fiscal 13%:
            </Typography>
            <Typography variant="body2" color="primary.main" fontWeight={700}>
              +${resumenTotales.montoIva.toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Renglón de Retención 1% */}
        {tipoDte === '03' && resumenTotales.montoRetencion1 > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="warning.main" fontWeight={600}>
              (-) 1% Retención IVA:
            </Typography>
            <Typography variant="body2" color="warning.main" fontWeight={700}>
              -${resumenTotales.montoRetencion1.toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Informativo IVA incluido Consumidor Final */}
        {tipoDte === '01' && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              (Incluye IVA 13%):
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              ${resumenTotales.montoIva.toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Factura de Exportación */}
        {tipoDte === '11' && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              IVA Exportación (0%):
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              $0.00
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 1.5, borderColor: alpha(theme.palette.divider, 0.8) }} />

        {/* Bloque Destacado de Totales */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2.5,
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={800}>
              TOTAL GENERAL:
            </Typography>
            <Typography variant="subtitle1" fontWeight={800} color="primary.main">
              ${resumenTotales.totalGeneral.toFixed(2)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total Abonado:
            </Typography>
            <Typography variant="body2" color="success.main" fontWeight={700}>
              ${resumenTotales.totalAbonado.toFixed(2)}
            </Typography>
          </Box>

          <Divider sx={{ my: 1, borderColor: alpha(theme.palette.divider, 0.6) }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Saldo Pendiente:
            </Typography>
            <Typography
              variant="subtitle2"
              fontWeight={800}
              color={resumenTotales.saldoPendiente > 0 ? 'error.main' : 'text.primary'}
            >
              ${resumenTotales.saldoPendiente.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        {/* Botón Acción Calculadora */}
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          size="medium"
          startIcon={<CalculateIcon />}
          onClick={onAbrirCalculadora}
          sx={{
            py: 1.2,
            borderRadius: 2.5,
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: `0 6px 14px ${alpha(theme.palette.secondary.main, 0.25)}`,
          }}
        >
          Calcular Cambio (F2)
        </Button>
      </Box>
    </Paper>
  );
};

export default AlbaranResumenPanel;